import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td, IconButton, Input, InputGroup,
    HStack, VStack, useToast, Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalCloseButton, ModalBody, ModalFooter, FormControl, FormLabel, Select, SimpleGrid,
    Spinner, Badge, InputLeftElement, Checkbox, AlertDialog, AlertDialogOverlay,
    AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import {
    getUsuarios, getUsuario, createUsuario, updateUsuario, toggleBloqueioUsuario,
    getPermissoesUsuario, updatePermissoesUsuario, getPaginasDisponiveis, getLogAcessos
} from "../services/api";
import { useDebounce } from "../hooks/useDebounce";
import { UserPlus, Eye, Pencil, ChevronLeft, ChevronRight, Shield, Save, History, Monitor, Globe, MapPin, Lock, Unlock } from "lucide-react";

const TIPO_LABELS = {
    1: { label: "Administrador", color: "red" },
    2: { label: "Vendedor", color: "blue" },
    3: { label: "Operações", color: "green" },
    4: { label: "Suporte", color: "purple" },
};

const Usuarios = () => {
    const toast = useToast();
    const usuarioLogadoId = JSON.parse(window.localStorage.getItem("user") || "null")?.id;

    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterTipo, setFilterTipo] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationMeta, setPaginationMeta] = useState(null);
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    // Form modal
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [formData, setFormData] = useState({ id: null, name: "", email: "", password: "", tipo: "1", phone: "", nvoip_numbersip: "", nvoip_senha: "" });
    const [processing, setProcessing] = useState(false);

    // View modal
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewData, setViewData] = useState(null);

    // Confirmacao de bloqueio
    const [bloqueioAlvo, setBloqueioAlvo] = useState(null);
    const [processandoBloqueio, setProcessandoBloqueio] = useState(false);
    const cancelBloqueioRef = useRef();

    // Logs de acesso modal
    const [isLogModalOpen, setIsLogModalOpen] = useState(false);
    const [logUser, setLogUser] = useState(null);
    const [logAcessos, setLogAcessos] = useState([]);
    const [loadingLog, setLoadingLog] = useState(false);
    const [logPage, setLogPage] = useState(1);
    const [logMeta, setLogMeta] = useState(null);

    // Permissoes modal
    const [isPermModalOpen, setIsPermModalOpen] = useState(false);
    const [permUser, setPermUser] = useState(null);
    const [paginasDisponiveis, setPaginasDisponiveis] = useState([]);
    const [permissoesAtivas, setPermissoesAtivas] = useState([]);
    const [loadingPerm, setLoadingPerm] = useState(false);
    const [savingPerm, setSavingPerm] = useState(false);

    const fetchUsuarios = useCallback(async () => {
        setLoading(true);
        const params = { page: currentPage, search: debouncedSearchTerm };
        if (filterTipo) params.tipo = filterTipo;
        const response = await getUsuarios(params);
        if (response) {
            setUsuarios(response.data);
            setPaginationMeta({
                current_page: response.current_page,
                last_page: response.last_page,
                total: response.total,
            });
        }
        setLoading(false);
    }, [currentPage, debouncedSearchTerm, filterTipo]);

    useEffect(() => { fetchUsuarios(); }, [fetchUsuarios]);
    useEffect(() => { setCurrentPage(1); }, [debouncedSearchTerm, filterTipo]);

    // Carrega paginas disponiveis uma vez
    useEffect(() => {
        const load = async () => {
            const res = await getPaginasDisponiveis();
            if (res && res.data) setPaginasDisponiveis(res.data);
        };
        load();
    }, []);

    const handleFormChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

    const openCreateModal = () => {
        setFormData({ id: null, name: "", email: "", password: "", tipo: "1", phone: "", nvoip_numbersip: "", nvoip_senha: "" });
        setIsFormModalOpen(true);
    };

    const openEditModal = async (usuario) => {
        const res = await getUsuario(usuario.id);
        if (res && res.data) {
            const u = res.data;
            setFormData({
                id: u.id, name: u.name, email: u.email, password: "", tipo: String(u.tipo), phone: u.phone || "",
                nvoip_numbersip: u.nvoip_numbersip || "", nvoip_senha: "",
            });
            setIsFormModalOpen(true);
        }
    };

    const openViewModal = async (usuario) => {
        const res = await getUsuario(usuario.id);
        if (res && res.data) {
            setViewData(res.data);
            setIsViewModalOpen(true);
        }
    };

    const handleSave = async () => {
        if (!formData.name || !formData.email) {
            toast({ title: "Preencha nome e email", status: "warning", duration: 3000 });
            return;
        }
        if (!formData.id && !formData.password) {
            toast({ title: "Senha obrigatória para novo usuário", status: "warning", duration: 3000 });
            return;
        }

        setProcessing(true);
        const payload = {
            name: formData.name, email: formData.email, tipo: parseInt(formData.tipo), phone: formData.phone || null,
            nvoip_numbersip: formData.nvoip_numbersip || null,
        };
        if (formData.password) payload.password = formData.password;
        if (formData.nvoip_senha) payload.nvoip_senha = formData.nvoip_senha;

        const response = formData.id
            ? await updateUsuario(formData.id, payload)
            : await createUsuario(payload);

        setProcessing(false);

        // Erro do axios volta como e.response, que TAMBEM tem .data — checar .data
        // dava "Usuário criado" em cima de um 500. Sucesso e o que traz message.
        if (response && response.message) {
            toast({ title: formData.id ? "Usuário atualizado" : "Usuário criado", status: "success", duration: 3000 });
            setIsFormModalOpen(false);
            fetchUsuarios();
        } else {
            toast({ title: response?.data?.error || "Erro ao salvar usuário", status: "error", duration: 4000 });
        }
    };

    // ── Bloqueio de acesso ──
    const confirmarBloqueio = async () => {
        if (!bloqueioAlvo) return;
        const bloquear = !bloqueioAlvo.bloqueado;

        setProcessandoBloqueio(true);
        const res = await toggleBloqueioUsuario(bloqueioAlvo.id, bloquear);
        setProcessandoBloqueio(false);

        if (res && res.message) {
            toast({ title: res.message, status: "success", duration: 3000 });
            setBloqueioAlvo(null);
            fetchUsuarios();
        } else {
            toast({ title: res?.data?.error || "Erro ao alterar bloqueio", status: "error", duration: 4000 });
        }
    };

    // ── Logs de Acesso ──
    const openLogModal = (usuario) => {
        setLogUser(usuario);
        setLogPage(1);
        setIsLogModalOpen(true);
    };

    const fetchLogs = useCallback(async () => {
        if (!logUser) return;
        setLoadingLog(true);
        const res = await getLogAcessos({ user_id: logUser.id, page: logPage, limit: 10 });
        if (res) {
            setLogAcessos(res.data);
            setLogMeta({ current_page: res.current_page, last_page: res.last_page, total: res.total });
        }
        setLoadingLog(false);
    }, [logUser, logPage]);

    useEffect(() => { if (isLogModalOpen) fetchLogs(); }, [fetchLogs, isLogModalOpen]);

    // ── Permissoes ──
    const openPermModal = async (usuario) => {
        setPermUser(usuario);
        setIsPermModalOpen(true);
        setLoadingPerm(true);
        const res = await getPermissoesUsuario(usuario.id);
        setPermissoesAtivas(res?.data?.permissoes || []);
        setLoadingPerm(false);
    };

    const togglePermissao = (pagina) => {
        setPermissoesAtivas(prev => prev.includes(pagina) ? prev.filter(p => p !== pagina) : [...prev, pagina]);
    };

    const toggleGrupo = (grupo) => {
        const paginasGrupo = paginasDisponiveis.filter(p => p.grupo === grupo).map(p => p.path);
        const todasAtivas = paginasGrupo.every(p => permissoesAtivas.includes(p));
        if (todasAtivas) {
            setPermissoesAtivas(prev => prev.filter(p => !paginasGrupo.includes(p)));
        } else {
            setPermissoesAtivas(prev => [...prev, ...paginasGrupo.filter(p => !prev.includes(p))]);
        }
    };

    const handleSavePerm = async () => {
        if (!permUser) return;
        setSavingPerm(true);
        const res = await updatePermissoesUsuario(permUser.id, permissoesAtivas);
        setSavingPerm(false);
        if (res && res.permissoes !== undefined) {
            toast({ title: "Permissões salvas", description: `${permissoesAtivas.length} página(s) para ${permUser.name}`, status: "success", duration: 3000 });
            setIsPermModalOpen(false);
        } else {
            toast({ title: "Erro ao salvar permissões", status: "error", duration: 4000 });
        }
    };

    // Agrupa paginas
    const grupos = paginasDisponiveis.reduce((acc, p) => {
        if (!acc[p.grupo]) acc[p.grupo] = [];
        acc[p.grupo].push(p);
        return acc;
    }, {});

    const ml = { base: "0", md: "64px" };
    const px = { base: 3, md: 6 };

    return (
        <Box ml={ml} p={px} pt="75px" minH="100vh" bg="gray.50">
            {/* Header */}
            <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
                <Box>
                    <Text fontSize="xl" fontWeight="bold" color="gray.800">Usuários</Text>
                    <Text fontSize="sm" color="gray.500">
                        Gerenciar usuários e permissões
                        {paginationMeta && ` — ${paginationMeta.total} usuário(s)`}
                    </Text>
                </Box>
                <Button leftIcon={<UserPlus size={16} />} colorScheme="orange" size="sm" onClick={openCreateModal}>
                    Novo Usuário
                </Button>
            </Flex>

            {/* Filters */}
            <Flex mb={4} gap={3} flexWrap="wrap">
                <InputGroup maxW="350px" size="sm">
                    <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" boxSize={3} /></InputLeftElement>
                    <Input placeholder="Buscar por nome ou email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} bg="white" borderRadius="lg" />
                </InputGroup>
                <Select maxW="180px" size="sm" bg="white" borderRadius="lg" value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)} placeholder="Todos os tipos">
                    <option value="1">Administrador</option>
                    <option value="2">Vendedor</option>
                    <option value="3">Operações</option>
                    <option value="4">Suporte</option>
                </Select>
            </Flex>

            {/* Table */}
            <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden">
                {loading ? (
                    <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex>
                ) : usuarios.length === 0 ? (
                    <Text textAlign="center" py={10} color="gray.400" fontSize="sm">Nenhum usuário encontrado</Text>
                ) : (
                    <Box overflowX="auto">
                        <Table size="sm" variant="simple">
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th fontSize="xs" color="gray.500">ID</Th>
                                    <Th fontSize="xs" color="gray.500">Nome</Th>
                                    <Th fontSize="xs" color="gray.500">Email</Th>
                                    <Th fontSize="xs" color="gray.500">Telefone</Th>
                                    <Th fontSize="xs" color="gray.500">Tipo</Th>
                                    <Th fontSize="xs" color="gray.500">Status</Th>
                                    <Th fontSize="xs" color="gray.500" textAlign="center">Ações</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {usuarios.map((u) => {
                                    const tipoInfo = TIPO_LABELS[u.tipo] || { label: "Desconhecido", color: "gray" };
                                    return (
                                        <Tr key={u.id} _hover={{ bg: "gray.50" }}>
                                            <Td fontSize="sm" color="gray.600">{u.id}</Td>
                                            <Td fontSize="sm" fontWeight="medium">{u.name}</Td>
                                            <Td fontSize="sm" color="gray.600">{u.email}</Td>
                                            <Td fontSize="sm" color="gray.600">{u.phone || "—"}</Td>
                                            <Td>
                                                <Badge colorScheme={tipoInfo.color} fontSize="10px" borderRadius="md" px={2}>
                                                    {tipoInfo.label}
                                                </Badge>
                                            </Td>
                                            <Td>
                                                <Badge colorScheme={u.bloqueado ? "red" : "green"} fontSize="10px" borderRadius="md" px={2}>
                                                    {u.bloqueado ? "Bloqueado" : "Ativo"}
                                                </Badge>
                                            </Td>
                                            <Td textAlign="center">
                                                <HStack spacing={1} justify="center">
                                                    <IconButton icon={<Eye size={15} />} size="xs" variant="ghost" colorScheme="blue" aria-label="Visualizar" onClick={() => openViewModal(u)} />
                                                    <IconButton icon={<Pencil size={15} />} size="xs" variant="ghost" colorScheme="orange" aria-label="Editar" onClick={() => openEditModal(u)} />
                                                    <IconButton icon={<Shield size={15} />} size="xs" variant="ghost" colorScheme="purple" aria-label="Permissões" onClick={() => openPermModal(u)} />
                                                    <IconButton icon={<History size={15} />} size="xs" variant="ghost" colorScheme="teal" aria-label="Histórico de Acessos" onClick={() => openLogModal(u)} />
                                                    <IconButton
                                                        icon={u.bloqueado ? <Unlock size={15} /> : <Lock size={15} />}
                                                        size="xs"
                                                        variant="ghost"
                                                        colorScheme={u.bloqueado ? "green" : "red"}
                                                        aria-label={u.bloqueado ? "Desbloquear usuário" : "Bloquear usuário"}
                                                        title={u.id === usuarioLogadoId ? "Você não pode bloquear seu próprio usuário" : (u.bloqueado ? "Desbloquear usuário" : "Bloquear usuário")}
                                                        isDisabled={u.id === usuarioLogadoId}
                                                        onClick={() => setBloqueioAlvo(u)}
                                                    />
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </Box>
                )}

                {/* Pagination */}
                {paginationMeta && paginationMeta.last_page > 1 && (
                    <Flex justify="space-between" align="center" px={4} py={3} borderTop="1px solid" borderColor="gray.100">
                        <Text fontSize="xs" color="gray.500">Página {paginationMeta.current_page} de {paginationMeta.last_page}</Text>
                        <HStack spacing={2}>
                            <IconButton icon={<ChevronLeft size={16} />} size="xs" variant="outline" isDisabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} aria-label="Anterior" />
                            <IconButton icon={<ChevronRight size={16} />} size="xs" variant="outline" isDisabled={currentPage >= paginationMeta.last_page} onClick={() => setCurrentPage(p => p + 1)} aria-label="Próxima" />
                        </HStack>
                    </Flex>
                )}
            </Box>

            {/* ── Modal Criar/Editar ── */}
            <Modal isOpen={isFormModalOpen} onClose={() => setIsFormModalOpen(false)} size="lg" isCentered>
                <ModalOverlay />
                <ModalContent borderRadius="xl">
                    <ModalHeader fontSize="md" pb={2}>{formData.id ? "Editar Usuário" : "Novo Usuário"}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={4}>
                        <SimpleGrid columns={[1, 2]} spacing={4}>
                            <FormControl isRequired>
                                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Nome</FormLabel>
                                <Input size="sm" borderRadius="lg" value={formData.name} onChange={(e) => handleFormChange("name", e.target.value)} placeholder="Nome completo" />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Email</FormLabel>
                                <Input size="sm" borderRadius="lg" type="email" value={formData.email} onChange={(e) => handleFormChange("email", e.target.value)} placeholder="email@exemplo.com" />
                            </FormControl>
                            <FormControl isRequired={!formData.id}>
                                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">
                                    Senha {formData.id && <Text as="span" fontWeight="normal" color="gray.400">(deixe vazio para manter)</Text>}
                                </FormLabel>
                                <Input size="sm" borderRadius="lg" type="password" value={formData.password} onChange={(e) => handleFormChange("password", e.target.value)} placeholder={formData.id ? "••••••" : "Mínimo 6 caracteres"} />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Tipo</FormLabel>
                                <Select size="sm" borderRadius="lg" value={formData.tipo} onChange={(e) => handleFormChange("tipo", e.target.value)}>
                                    <option value="1">Administrador</option>
                                    <option value="2">Vendedor</option>
                                    <option value="3">Operações</option>
                                    <option value="4">Suporte</option>
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Telefone</FormLabel>
                                <Input size="sm" borderRadius="lg" value={formData.phone} onChange={(e) => handleFormChange("phone", e.target.value)} placeholder="(11) 99999-9999" />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">NumberSIP NVOIP</FormLabel>
                                <Input size="sm" borderRadius="lg" value={formData.nvoip_numbersip} onChange={(e) => handleFormChange("nvoip_numbersip", e.target.value)} placeholder="Ex: 116364002" />
                            </FormControl>
                            <FormControl gridColumn={[ "auto", "span 2" ]}>
                                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">
                                    Senha SIP NVOIP {formData.id && <Text as="span" fontWeight="normal" color="gray.400">(deixe vazio para manter)</Text>}
                                </FormLabel>
                                <Input size="sm" borderRadius="lg" type="password" autoComplete="new-password" value={formData.nvoip_senha} onChange={(e) => handleFormChange("nvoip_senha", e.target.value)} placeholder={formData.id ? "••••••" : "Senha do ramal SIP"} />
                            </FormControl>
                        </SimpleGrid>
                    </ModalBody>
                    <ModalFooter pt={0}>
                        <Button size="sm" variant="ghost" mr={2} onClick={() => setIsFormModalOpen(false)}>Cancelar</Button>
                        <Button size="sm" colorScheme="orange" onClick={handleSave} isLoading={processing}>{formData.id ? "Salvar" : "Criar"}</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* ── Modal Visualizar ── */}
            <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} size="md" isCentered>
                <ModalOverlay />
                <ModalContent borderRadius="xl">
                    <ModalHeader fontSize="md" pb={2}>Detalhes do Usuário</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {viewData && (
                            <VStack spacing={3} align="stretch">
                                <DetailRow label="ID" value={viewData.id} />
                                <DetailRow label="Nome" value={viewData.name} />
                                <DetailRow label="Email" value={viewData.email} />
                                <DetailRow label="Telefone" value={viewData.phone || "—"} />
                                <DetailRow label="Tipo" value={
                                    <Badge colorScheme={TIPO_LABELS[viewData.tipo]?.color || "gray"} fontSize="10px" borderRadius="md" px={2}>
                                        {TIPO_LABELS[viewData.tipo]?.label || "Desconhecido"}
                                    </Badge>
                                } />
                                <DetailRow label="Permissões" value={
                                    viewData.permissoes && viewData.permissoes.length > 0
                                        ? `${viewData.permissoes.length} página(s)`
                                        : "Nenhuma permissão configurada"
                                } />
                                <DetailRow label="Criado em" value={viewData.created_at ? new Date(viewData.created_at).toLocaleDateString("pt-BR") : "—"} />
                            </VStack>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* ── Modal Logs de Acesso ── */}
            <Modal isOpen={isLogModalOpen} onClose={() => { setIsLogModalOpen(false); setLogUser(null); }} size="4xl" isCentered scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent borderRadius="xl" maxH="85vh">
                    <ModalHeader fontSize="md" pb={1}>
                        <Flex align="center" gap={2}>
                            <History size={18} />
                            <Text>Histórico de Acessos — {logUser?.name}</Text>
                        </Flex>
                        {logMeta && <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={1}>{logMeta.total} acesso(s) registrado(s)</Text>}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={4}>
                        {loadingLog ? (
                            <Flex justify="center" py={8}><Spinner color="orange.500" /></Flex>
                        ) : logAcessos.length === 0 ? (
                            <Text textAlign="center" py={8} color="gray.400" fontSize="sm">Nenhum acesso registrado</Text>
                        ) : (
                            <VStack spacing={3} align="stretch">
                                {logAcessos.map((log) => (
                                    <Box key={log.id} border="1px solid" borderColor="gray.200" borderRadius="lg" p={3} _hover={{ borderColor: "gray.300" }}>
                                        <Flex justify="space-between" align="start" flexWrap="wrap" gap={2}>
                                            <Box flex="1" minW="200px">
                                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                    {new Date(log.created_at).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}
                                                    {" "}
                                                    <Text as="span" color="orange.500">{new Date(log.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</Text>
                                                </Text>
                                                <HStack spacing={4} mt={1.5} flexWrap="wrap">
                                                    <HStack spacing={1}>
                                                        <Globe size={12} color="#718096" />
                                                        <Text fontSize="xs" color="gray.600">{log.ip || "—"}</Text>
                                                    </HStack>
                                                    <HStack spacing={1}>
                                                        <Monitor size={12} color="#718096" />
                                                        <Text fontSize="xs" color="gray.600">{log.dispositivo || "—"} / {log.navegador || "—"}</Text>
                                                    </HStack>
                                                    {log.sistema_operacional && (
                                                        <Text fontSize="xs" color="gray.500">{log.sistema_operacional}</Text>
                                                    )}
                                                </HStack>
                                            </Box>
                                            {(log.cidade || log.regiao || log.pais) && (
                                                <HStack spacing={1} alignSelf="center">
                                                    <MapPin size={12} color="#718096" />
                                                    <Text fontSize="xs" color="gray.500">
                                                        {[log.cidade, log.regiao, log.pais].filter(Boolean).join(", ")}
                                                    </Text>
                                                </HStack>
                                            )}
                                        </Flex>
                                    </Box>
                                ))}
                            </VStack>
                        )}
                    </ModalBody>
                    {logMeta && logMeta.last_page > 1 && (
                        <ModalFooter borderTop="1px solid" borderColor="gray.100" justifyContent="space-between">
                            <Text fontSize="xs" color="gray.500">Página {logMeta.current_page} de {logMeta.last_page}</Text>
                            <HStack spacing={2}>
                                <IconButton icon={<ChevronLeft size={16} />} size="xs" variant="outline" isDisabled={logPage <= 1} onClick={() => setLogPage(p => p - 1)} aria-label="Anterior" />
                                <IconButton icon={<ChevronRight size={16} />} size="xs" variant="outline" isDisabled={logPage >= logMeta.last_page} onClick={() => setLogPage(p => p + 1)} aria-label="Próxima" />
                            </HStack>
                        </ModalFooter>
                    )}
                </ModalContent>
            </Modal>

            {/* ── Modal Permissões ── */}
            <Modal isOpen={isPermModalOpen} onClose={() => setIsPermModalOpen(false)} size="2xl" isCentered scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent borderRadius="xl" maxH="85vh">
                    <ModalHeader fontSize="md" pb={1}>
                        <Flex align="center" gap={2}>
                            <Shield size={18} />
                            <Text>Permissões — {permUser?.name}</Text>
                        </Flex>
                        {permUser && (
                            <Badge colorScheme={TIPO_LABELS[permUser.tipo]?.color} fontSize="10px" mt={1}>
                                {TIPO_LABELS[permUser.tipo]?.label}
                            </Badge>
                        )}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={4}>
                        {loadingPerm ? (
                            <Flex justify="center" py={8}><Spinner color="orange.500" /></Flex>
                        ) : (
                            <>
                                <Flex gap={2} mb={4}>
                                    <Button size="xs" variant="outline" colorScheme="green" onClick={() => setPermissoesAtivas(paginasDisponiveis.map(p => p.path))}>
                                        Selecionar Todas
                                    </Button>
                                    <Button size="xs" variant="outline" colorScheme="red" onClick={() => setPermissoesAtivas([])}>
                                        Desmarcar Todas
                                    </Button>
                                    <Text ml="auto" fontSize="xs" color="gray.500" alignSelf="center">
                                        {permissoesAtivas.length} de {paginasDisponiveis.length} selecionadas
                                    </Text>
                                </Flex>

                                <VStack spacing={4} align="stretch">
                                    {Object.entries(grupos).map(([grupo, paginas]) => {
                                        const todasAtivas = paginas.every(p => permissoesAtivas.includes(p.path));
                                        const algumasAtivas = paginas.some(p => permissoesAtivas.includes(p.path));
                                        return (
                                            <Box key={grupo} border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                                                <Flex
                                                    align="center" justify="space-between" px={4} py={2.5} bg="gray.50"
                                                    cursor="pointer" onClick={() => toggleGrupo(grupo)} _hover={{ bg: "gray.100" }}
                                                >
                                                    <HStack spacing={2}>
                                                        <Checkbox
                                                            isChecked={todasAtivas} isIndeterminate={!todasAtivas && algumasAtivas}
                                                            colorScheme="orange" size="sm"
                                                            onChange={() => toggleGrupo(grupo)} onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <Text fontSize="sm" fontWeight="bold" color="gray.700">{grupo}</Text>
                                                        <Badge colorScheme="gray" fontSize="9px" borderRadius="full">
                                                            {paginas.filter(p => permissoesAtivas.includes(p.path)).length}/{paginas.length}
                                                        </Badge>
                                                    </HStack>
                                                </Flex>
                                                <VStack spacing={0} align="stretch" px={4} py={2}>
                                                    {paginas.map((pagina) => {
                                                        const ativa = permissoesAtivas.includes(pagina.path);
                                                        return (
                                                            <Flex
                                                                key={pagina.path} align="center" justify="space-between"
                                                                py={1.5} px={2} borderRadius="md" _hover={{ bg: "gray.50" }}
                                                                cursor="pointer" onClick={() => togglePermissao(pagina.path)}
                                                            >
                                                                <HStack spacing={3}>
                                                                    <Checkbox isChecked={ativa} colorScheme="orange" size="sm" onChange={() => togglePermissao(pagina.path)} onClick={(e) => e.stopPropagation()} />
                                                                    <Box>
                                                                        <Text fontSize="sm" color={ativa ? "gray.800" : "gray.500"}>{pagina.label}</Text>
                                                                        <Text fontSize="10px" color="gray.400" fontFamily="mono">{pagina.path}</Text>
                                                                    </Box>
                                                                </HStack>
                                                            </Flex>
                                                        );
                                                    })}
                                                </VStack>
                                            </Box>
                                        );
                                    })}
                                </VStack>
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="gray.100">
                        <Button size="sm" variant="ghost" mr={2} onClick={() => setIsPermModalOpen(false)}>Cancelar</Button>
                        <Button leftIcon={<Save size={14} />} size="sm" colorScheme="orange" onClick={handleSavePerm} isLoading={savingPerm}>
                            Salvar Permissões
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* ── Confirmacao de bloqueio/desbloqueio ── */}
            <AlertDialog isOpen={!!bloqueioAlvo} leastDestructiveRef={cancelBloqueioRef} onClose={() => setBloqueioAlvo(null)} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent mx={4} borderRadius="xl">
                        <AlertDialogHeader fontSize="md" pb={2}>
                            {bloqueioAlvo?.bloqueado ? "Desbloquear usuário" : "Bloquear usuário"}
                        </AlertDialogHeader>
                        <AlertDialogBody fontSize="sm" color="gray.600">
                            {bloqueioAlvo?.bloqueado ? (
                                <>Liberar o acesso de <b>{bloqueioAlvo?.name}</b>? Ele volta a conseguir entrar no sistema.</>
                            ) : (
                                <>Bloquear o acesso de <b>{bloqueioAlvo?.name}</b>? Ele será deslogado de todos os dispositivos e não conseguirá mais entrar, nem por senha nem pelo código do WhatsApp.</>
                            )}
                        </AlertDialogBody>
                        <AlertDialogFooter gap={2}>
                            <Button ref={cancelBloqueioRef} size="sm" onClick={() => setBloqueioAlvo(null)}>Cancelar</Button>
                            <Button
                                size="sm"
                                colorScheme={bloqueioAlvo?.bloqueado ? "green" : "red"}
                                onClick={confirmarBloqueio}
                                isLoading={processandoBloqueio}
                            >
                                {bloqueioAlvo?.bloqueado ? "Desbloquear" : "Bloquear e deslogar"}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

const DetailRow = ({ label, value }) => (
    <Flex justify="space-between" align="center" py={1.5} px={2} borderRadius="md" bg="gray.50">
        <Text fontSize="xs" fontWeight="semibold" color="gray.500">{label}</Text>
        <Text fontSize="sm" fontWeight="medium" color="gray.800">{value}</Text>
    </Flex>
);

export default Usuarios;
